import { MultiSelect } from '@lmc/ui-kit'
import type {
  GroupedOption,
  Option
} from '@lmc/ui-kit/src/dataEntry/MultiSelect/MultiSelect'
import { Text } from '@lmc/ui-kit/src/general'
import { Form } from 'antd'
import type { RuleObject } from 'antd/es/form'
import { showToast } from 'common/utils'
import { useEffect, useRef, useState } from 'react'
import { getTeamMemberFormId } from '../RotaDrawer/utils'

// Wrapper component to adapt Form.Item's value/onChange to MultiSelect's values/onChange
export const TeamMembersMultiSelect = ({
  value,
  onChange,
  title,
  groupedOptions,
  formData,
  teamMembersCount,
  maxTeamMembers,
  validator
}: {
  value?: (string | Option)[]
  onChange?: (values: (string | Option)[]) => void
  title: string
  groupedOptions: GroupedOption<Option>[]
  formData?: Record<string, unknown>
  teamMembersCount: number
  maxTeamMembers: number
  validator?: (rule: RuleObject, value: string[]) => Promise<void>
}) => {
  const form = Form.useFormInstance()
  const previousValueRef = useRef<(string | Option)[]>(
    value || (formData?.teamMembers as (string | Option)[] | undefined) || []
  )
  const isValidationPendingRef = useRef(false)
  const isRevertingRef = useRef(false)
  const [revertKey, setRevertKey] = useState(0)

  // Update ref when value changes (validation passed)
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const currentValue: (string | Option)[] =
      value || (formData?.teamMembers as (string | Option)[] | undefined) || []
    if (!isValidationPendingRef.current && !isRevertingRef.current) {
      previousValueRef.current = currentValue
    }
    // Reset reverting flag after value updates
    if (isRevertingRef.current) {
      isRevertingRef.current = false
    }
  }, [value, formData])

  const displayValue =
    value || (formData?.teamMembers as (string | Option)[] | undefined) || []
  const mapIdsToDisplayValue = (
    ids: string[],
    previousValue: (string | Option)[],
    keepObjectFormat: boolean
  ) => {
    if (!keepObjectFormat) {
      return ids
    }
    return ids.map((id: string) => {
      const obj = previousValue.find((m) => getTeamMemberFormId(m) === id)
      if (obj) {
        return obj
      }
      if (groupedOptions) {
        for (const group of groupedOptions) {
          const option = group.options?.find((opt) => opt.value === id)
          if (option) {
            return { value: option.value, label: option.label }
          }
        }
      }
      return { value: id, label: id }
    })
  }
  // Use a combination of value and revert key to force re-render on revert
  const componentKey = `${JSON.stringify(displayValue)}-${revertKey}`

  return (
    <MultiSelect
      key={componentKey} // Force re-render when value changes/reverts
      title={title}
      groupedOptions={groupedOptions}
      hasClear
      hasSearch
      hasSelectAll
      placeholder
      maxTagCount={null}
      values={displayValue}
      onChange={async (newValues) => {
        // Skip validation if we're reverting
        if (isRevertingRef.current) {
          return
        }

        // MultiSelect always passes IDs (strings) in newValues, not objects
        // But the form might store them as objects, so we need to check the format of displayValue
        const isObjectFormat =
          Array.isArray(displayValue) &&
          displayValue.length > 0 &&
          typeof displayValue[0] === 'object'

        // Normalize newValues to ensure consistent format (array of IDs/values)
        const normalizedNewValues = Array.isArray(newValues)
          ? newValues.map(getTeamMemberFormId)
          : []

        // Normalize previousValue to ensure consistent format
        const previousValue = previousValueRef.current

        // Validate before updating
        if (validator) {
          isValidationPendingRef.current = true
          try {
            // Create a mock rule object for the validator
            const mockRule = {} as RuleObject
            await validator(mockRule, normalizedNewValues)
            // Validation passed - update the value
            // Store in the same format as displayValue
            previousValueRef.current = mapIdsToDisplayValue(
              normalizedNewValues,
              previousValue,
              isObjectFormat
            )
            onChange?.(previousValueRef.current)
          } catch (error) {
            // Validation failed - get the specific clocked-in team member IDs from the error
            const clockedInIds =
              (error as { clockedInIds?: string[] })?.clockedInIds || []

            // Only add back the team members that are actually clocked in
            // This ensures we only restore the ones that shouldn't be removed, not all removed members
            const restoredIds = [...normalizedNewValues, ...clockedInIds]

            // Convert back to the format that the form expects (same as displayValue)
            const formatToRestore = isObjectFormat
              ? restoredIds.map((id: string) => {
                  // Try to find the original object from previousValue to preserve label/name
                  const originalObj = previousValue.find(
                    (m) => getTeamMemberFormId(m) === id
                  )
                  // If we found the original object, use it to preserve the label
                  if (originalObj) {
                    return originalObj
                  }
                  // If not found, try to find it in groupedOptions to get the label
                  if (groupedOptions) {
                    for (const group of groupedOptions) {
                      const option = group.options?.find(
                        (opt) => opt.value === id
                      )
                      if (option) {
                        return { value: option.value, label: option.label }
                      }
                    }
                  }
                  // Fallback: create object with ID as label
                  return { value: id, label: id }
                })
              : restoredIds

            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Some team members are already clocked in'
            // Show error message to user
            showToast({
              type: 'warning',
              title: errorMessage
            })
            // Set reverting flag to skip validation on revert
            isRevertingRef.current = true
            // Clear any validation errors for this field
            form.setFields([{ name: 'teamMembers', errors: [] }])
            // Restore only the team members that are clocked in
            previousValueRef.current = formatToRestore
            onChange?.(formatToRestore)
            // Wait a tick for form to update, then increment revert key to force MultiSelect re-render
            setTimeout(() => {
              setRevertKey((prev) => prev + 1)
              isRevertingRef.current = false
            }, 0)
          } finally {
            isValidationPendingRef.current = false
          }
        } else {
          // No validator - just update
          const normalizedNewValues = Array.isArray(newValues)
            ? newValues.map(getTeamMemberFormId)
            : []
          const previousValue = previousValueRef.current
          const nextValue = mapIdsToDisplayValue(
            normalizedNewValues,
            Array.isArray(previousValue) ? previousValue : [],
            isObjectFormat
          )
          previousValueRef.current = nextValue
          onChange?.(nextValue)
        }
      }}
      suffixIcon={
        <Text>
          {teamMembersCount}/{maxTeamMembers}
        </Text>
      }
    />
  )
}
